import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';

interface StripeContextType {
  stripePromise: Promise<Stripe | null> | null;
  isLoading: boolean;
  error: string | null;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

interface StripeProviderProps {
  children: ReactNode;
}

interface StripeConfigResponse {
  success: boolean;
  publishable_key?: string;
  error?: string;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStripeConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch('/api/stripe-config', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data: StripeConfigResponse = await response.json();

        if (response.ok && data.success && data.publishable_key) {
          const promise = loadStripe(data.publishable_key);
          setStripePromise(promise);
        } else {
          const errorMsg = data.error || 'Failed to load Stripe configuration';
          setError(errorMsg);
        }
      } catch (err) {
        const errorMsg = 'Unable to connect to payment service';
        setError(errorMsg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStripeConfig();
  }, []);

  return (
    <StripeContext.Provider value={{ stripePromise, isLoading, error }}>
      {children}
    </StripeContext.Provider>
  );
};

export const useStripe = (): StripeContextType => {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

export default StripeContext;
